import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Settings2, Plus, Trash2, Pencil, X, Loader2, Check, Filter, Search, Ban } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type MatchType = "starts_with" | "contains" | "equals" | "regex";
type RuleAction = "assign" | "exclude";

interface Rule {
  id: number;
  matchType: MatchType;
  pattern: string;
  account: string | null;
  action: RuleAction;
  priority: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const MATCH_LABELS: Record<MatchType, string> = {
  starts_with: "Starts with",
  contains: "Contains",
  equals: "Equals",
  regex: "Regex",
};

interface FormState {
  matchType: MatchType;
  pattern: string;
  account: string;
  action: RuleAction;
  priority: string;
  notes: string;
}

const emptyForm: FormState = {
  matchType: "starts_with",
  pattern: "",
  account: "",
  action: "assign",
  priority: "100",
  notes: "",
};

const emptyExcludeForm: FormState = {
  ...emptyForm,
  action: "exclude",
  priority: "1",
};

export default function CashReceiptsRulesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [search, setSearch] = useState("");

  const { data: rules = [], isLoading } = useQuery<Rule[]>({
    queryKey: ["cash-receipts", "account-rules"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/account-rules`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load rules");
      return res.json();
    },
  });

  function startNew() {
    setForm(emptyForm);
    setEditing("new");
  }

  function startNewExclude() {
    setForm(emptyExcludeForm);
    setEditing("new");
  }

  function startEdit(rule: Rule) {
    setForm({
      matchType: rule.matchType,
      pattern: rule.pattern,
      account: rule.account ?? "",
      action: rule.action,
      priority: String(rule.priority),
      notes: rule.notes ?? "",
    });
    setEditing(rule.id);
  }

  function cancel() {
    setEditing(null);
    setForm(emptyForm);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const url = editing === "new"
        ? `${BASE}/api/cash-receipts/account-rules`
        : `${BASE}/api/cash-receipts/account-rules/${editing}`;
      const method = editing === "new" ? "POST" : "PATCH";
      const body = {
        matchType: form.matchType,
        pattern: form.pattern.trim(),
        account: form.action === "exclude" ? null : form.account.trim().toUpperCase(),
        action: form.action,
        priority: form.priority === "" ? 100 : Number(form.priority),
        notes: form.notes.trim() || null,
      };
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "account-rules"] });
      toast({ title: editing === "new" ? "Rule added" : "Rule updated" });
      cancel();
    },
    onError: (err: any) => toast({ title: err.message || "Save failed", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/account-rules/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "account-rules"] });
      toast({ title: "Rule deleted" });
    },
    onError: (err: any) => toast({ title: err.message || "Delete failed", variant: "destructive" }),
  });

  function handleDelete(rule: Rule) {
    const what = rule.action === "exclude"
      ? `Future bank payments matching "${rule.pattern}" will no longer be excluded.`
      : `Future bank payments will no longer be routed to ${rule.account} via "${rule.pattern}".`;
    if (!confirm(`Delete this rule? ${what}`)) return;
    deleteMutation.mutate(rule.id);
  }

  const canSave = form.pattern.trim() !== "" && (form.action === "exclude" || form.account.trim() !== "");

  const q = search.trim().toLowerCase();
  const filteredRules = q === ""
    ? rules
    : rules.filter((r) =>
        r.pattern.toLowerCase().includes(q) ||
        (r.account ?? "").toLowerCase().includes(q) ||
        (r.notes ?? "").toLowerCase().includes(q) ||
        (MATCH_LABELS[r.matchType] || r.matchType).toLowerCase().includes(q) ||
        (r.action === "exclude" && "exclude".includes(q)),
      );

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
              <Settings2 className="w-6 h-6 text-primary" />
              Customer Identification Rules
            </h1>
            <p className="text-sm text-duffells-grey/60 mt-1 max-w-2xl">
              When the BACS reconciler can't identify a customer from the narrative's hints, it applies
              these rules in priority order. First match wins. Example: narrative starts with "Huws Gray
              Limited" → account <span className="font-mono">HGL001</span>.
            </p>
          </div>
          {editing === null && (
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={startNewExclude} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
                <Ban className="w-4 h-4 mr-1" /> Exclude payments
              </Button>
              <Button onClick={startNew}>
                <Plus className="w-4 h-4 mr-1" /> Add rule
              </Button>
            </div>
          )}
        </div>

        {editing !== null && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-duffells-grey">
                {editing === "new" ? "New rule" : `Edit rule #${editing}`}
              </h2>
              <button onClick={cancel} className="text-duffells-grey/60 hover:text-duffells-grey">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-12">
                <label className="block text-xs font-medium text-duffells-grey/70 mb-1">Action</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, action: "assign", priority: form.action === "exclude" ? "100" : form.priority })}
                    className={`flex-1 text-sm rounded-lg px-3 py-2 border transition ${
                      form.action === "assign"
                        ? "border-primary bg-primary/5 text-primary font-medium"
                        : "border-slate-200 text-duffells-grey/70 hover:border-slate-300"
                    }`}
                  >
                    Assign to account
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, action: "exclude", account: "", priority: form.action === "assign" ? "1" : form.priority })}
                    className={`flex-1 text-sm rounded-lg px-3 py-2 border transition ${
                      form.action === "exclude"
                        ? "border-red-400 bg-red-50 text-red-700 font-medium"
                        : "border-slate-200 text-duffells-grey/70 hover:border-slate-300"
                    }`}
                  >
                    <Ban className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    Exclude (never reconcile)
                  </button>
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-duffells-grey/70 mb-1">Match type</label>
                <select
                  value={form.matchType}
                  onChange={(e) => setForm({ ...form, matchType: e.target.value as MatchType })}
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2 bg-white"
                >
                  {(Object.keys(MATCH_LABELS) as MatchType[]).map((mt) => (
                    <option key={mt} value={mt}>{MATCH_LABELS[mt]}</option>
                  ))}
                </select>
              </div>
              <div className={form.action === "exclude" ? "md:col-span-7" : "md:col-span-5"}>
                <label className="block text-xs font-medium text-duffells-grey/70 mb-1">
                  Pattern <span className="text-duffells-grey/40">(case-insensitive)</span>
                </label>
                <input
                  type="text"
                  value={form.pattern}
                  onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                  placeholder={form.action === "exclude" ? 'e.g. BANK CHARGES' : 'e.g. Huws Gray Limited'}
                  className="w-full text-sm font-mono border border-slate-200 rounded-lg px-2 py-2"
                />
              </div>
              {form.action === "assign" && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-duffells-grey/70 mb-1">Account</label>
                  <input
                    type="text"
                    value={form.account}
                    onChange={(e) => setForm({ ...form, account: e.target.value.toUpperCase() })}
                    placeholder="HGL001"
                    className="w-full text-sm font-mono border border-slate-200 rounded-lg px-2 py-2"
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-duffells-grey/70 mb-1">
                  Priority <span className="text-duffells-grey/40">(lower wins)</span>
                </label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2"
                />
              </div>
              <div className="md:col-span-12">
                <label className="block text-xs font-medium text-duffells-grey/70 mb-1">
                  Notes <span className="text-duffells-grey/40">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Why this rule exists, who confirmed the mapping, etc."
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!canSave || saveMutation.isPending}
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                {editing === "new" ? "Add rule" : "Save changes"}
              </Button>
              <Button variant="outline" onClick={cancel} disabled={saveMutation.isPending}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Active rules
            </h2>
            <div className="flex items-center gap-3 flex-1 justify-end min-w-[260px]">
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-duffells-grey/40 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search pattern, account, notes…"
                  className="w-full text-sm border border-slate-200 rounded-lg pl-8 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-duffells-grey/40 hover:text-duffells-grey"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <span className="text-xs text-duffells-grey/60 whitespace-nowrap">
                {q === "" ? `${rules.length} total` : `${filteredRules.length} of ${rules.length}`}
              </span>
            </div>
          </div>
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : rules.length === 0 ? (
            <div className="p-12 text-center text-duffells-grey/60 text-sm">
              No rules yet. Click <strong>Add rule</strong> above to create your first one.
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="p-12 text-center text-duffells-grey/60 text-sm">
              No rules match <span className="font-mono">"{search}"</span>.
              <button onClick={() => setSearch("")} className="ml-2 text-primary hover:underline">Clear search</button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
                <tr>
                  <th className="px-4 py-3 text-left w-12" title="Rule number — position in this list, not the database id.">#</th>
                  <th className="px-4 py-3 text-left w-16">Priority</th>
                  <th className="px-4 py-3 text-left w-32">Match type</th>
                  <th className="px-4 py-3 text-left">Pattern</th>
                  <th className="px-4 py-3 text-left w-28">→ Account</th>
                  <th className="px-4 py-3 text-left">Notes</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRules.map((rule, idx) => (
                  <tr key={rule.id} className={`hover:bg-slate-50 ${rule.action === "exclude" ? "bg-red-50/30" : ""}`}>
                    <td className="px-4 py-3 text-xs font-mono text-duffells-grey/60" title={`Database id: ${rule.id}`}>{idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-duffells-grey">{rule.priority}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                        {MATCH_LABELS[rule.matchType] || rule.matchType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-duffells-grey">{rule.pattern}</td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {rule.action === "exclude" ? (
                        <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                          <Ban className="w-3.5 h-3.5" /> EXCLUDE
                        </span>
                      ) : (
                        <span className="text-emerald-700">{rule.account}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-duffells-grey/70">{rule.notes || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button
                        onClick={() => startEdit(rule)}
                        className="text-duffells-grey/70 hover:text-primary p-1"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <AllocationPhrasebook />

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-xs text-duffells-grey/70 space-y-2">
          <p className="font-semibold text-duffells-grey/90">How resolution works</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Account hint parsed from the narrative is looked up in Merlin.</li>
            <li>If no hint, any invoice references in the narrative are looked up; if they all belong to one customer, that account is used.</li>
            <li>If still unidentified, the rules on this page are evaluated in priority order (lowest first); first match wins.</li>
            <li>If no rule matches, a fuzzy match against the customer.name list is attempted — accepted only when exactly one customer matches AND they have unpaid invoices on file.</li>
            <li>If all of the above fail, the payment lands in <em>Needs Review</em> and you can edit the account inline on the BACS page.</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}

// ---- Allocation rule phrases (LLM reply-parser phrasebook) ---------------
//
// Separate widget because the data model is much smaller than the account
// rules above (just {phrase, ruleKind, enabled, notes}) and the rule kinds
// are a fixed enum, not user-defined. We inline it on this page rather
// than create a new route so the whole "things ops can edit to influence
// auto-allocation" surface lives in one place.

type AllocationRuleKind = "oldest_first" | "newest_first" | "smallest_first" | "largest_first" | "pay_down_balance";

const RULE_KIND_LABELS: Record<AllocationRuleKind, string> = {
  oldest_first: "Oldest invoices first",
  newest_first: "Newest invoices first",
  smallest_first: "Smallest balances first",
  largest_first: "Largest balances first",
  pay_down_balance: "Pay down the balance (allows partial)",
};

const RULE_KINDS: AllocationRuleKind[] = ["oldest_first", "newest_first", "smallest_first", "largest_first", "pay_down_balance"];

interface AllocationPhrase {
  id: number;
  phrase: string;
  ruleKind: AllocationRuleKind;
  enabled: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

function AllocationPhrasebook() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newPhrase, setNewPhrase] = useState("");
  const [newKind, setNewKind] = useState<AllocationRuleKind>("oldest_first");
  const [newNotes, setNewNotes] = useState("");

  const { data: phrases = [], isLoading } = useQuery<AllocationPhrase[]>({
    queryKey: ["cash-receipts", "allocation-rules"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/allocation-rules`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load allocation phrases");
      return res.json();
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["cash-receipts", "allocation-rules"] });

  const addMutation = useMutation({
    mutationFn: async (body: { phrase: string; ruleKind: AllocationRuleKind; notes: string | null }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/allocation-rules`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, enabled: true }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Add failed");
      return res.json();
    },
    onSuccess: () => {
      setNewPhrase("");
      setNewNotes("");
      invalidate();
      toast({ title: "Phrase added" });
    },
    onError: (err: any) => toast({ title: "Failed to add phrase", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...body }: { id: number; phrase: string; ruleKind: AllocationRuleKind; enabled: boolean; notes: string | null }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/allocation-rules/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Update failed");
      return res.json();
    },
    onSuccess: () => invalidate(),
    onError: (err: any) => toast({ title: "Failed to update phrase", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/allocation-rules/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Delete failed");
    },
    onSuccess: () => { invalidate(); toast({ title: "Phrase removed" }); },
    onError: (err: any) => toast({ title: "Failed to delete phrase", description: err.message, variant: "destructive" }),
  });

  const handleAdd = () => {
    const phrase = newPhrase.trim();
    if (!phrase) return;
    addMutation.mutate({ phrase, ruleKind: newKind, notes: newNotes.trim() || null });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          Reply-parser phrasebook
        </h2>
        <p className="text-xs text-duffells-grey/60 mt-1">
          When a customer replies to a remittance-request email with a free-text instruction (e.g. "just pay the oldest first"),
          the LLM parser maps their wording to one of these strategies. Add or rephrase entries here — the parser learns the
          new vocabulary on its next run. The strategy types themselves are fixed (each maps to a server-side function).
        </p>
      </div>

      {/* Add row */}
      <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100 grid grid-cols-1 md:grid-cols-[1fr_220px_1fr_auto] gap-2 items-end">
        <div>
          <label className="block text-[11px] font-medium text-duffells-grey/70 mb-1">Phrase</label>
          <input
            type="text"
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder='e.g. "knock off the oldest"'
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-duffells-grey/70 mb-1">Maps to strategy</label>
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as AllocationRuleKind)}
            className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {RULE_KINDS.map((k) => (<option key={k} value={k}>{RULE_KIND_LABELS[k]}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-duffells-grey/70 mb-1">Notes (optional)</label>
          <input
            type="text"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            placeholder="why this phrase, examples, etc."
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <Button onClick={handleAdd} disabled={!newPhrase.trim() || addMutation.isPending}>
          {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : phrases.length === 0 ? (
        <div className="p-8 text-center text-duffells-grey/60 text-sm">No phrases yet. Add one above.</div>
      ) : (
        <table className="w-full">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
            <tr>
              <th className="px-4 py-3 text-left">Phrase</th>
              <th className="px-4 py-3 text-left w-56">Strategy</th>
              <th className="px-4 py-3 text-left">Notes</th>
              <th className="px-4 py-3 text-left w-20">On?</th>
              <th className="px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {phrases.map((p) => (
              <tr key={p.id} className={`hover:bg-slate-50 ${!p.enabled ? "opacity-50" : ""}`}>
                <td className="px-4 py-2 text-sm text-duffells-grey font-mono">{p.phrase}</td>
                <td className="px-4 py-2 text-xs">
                  <select
                    value={p.ruleKind}
                    onChange={(e) => updateMutation.mutate({ id: p.id, phrase: p.phrase, ruleKind: e.target.value as AllocationRuleKind, enabled: p.enabled, notes: p.notes })}
                    className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white"
                  >
                    {RULE_KINDS.map((k) => (<option key={k} value={k}>{RULE_KIND_LABELS[k]}</option>))}
                  </select>
                </td>
                <td className="px-4 py-2 text-xs text-duffells-grey/70">{p.notes || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={p.enabled}
                    onChange={(e) => updateMutation.mutate({ id: p.id, phrase: p.phrase, ruleKind: p.ruleKind, enabled: e.target.checked, notes: p.notes })}
                    className="w-4 h-4 accent-primary"
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => { if (confirm(`Delete phrase "${p.phrase}"?`)) deleteMutation.mutate(p.id); }}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete phrase"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
